import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  TablePagination,
  InputBase,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Search,
  CheckCircle2,
  Trash2,
  Clock,
  Mail,
  Phone,
  User,
  MessageSquare,
  RefreshCw,
  Send,
  HelpCircle,
  FileText,
} from 'lucide-react';
import axiosInstance from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions, DeleteConfirmDialog } from '../components/common/modal';
import EmptyState from '../components/common/EmptyState';
import { TableLoadingSkeleton } from '../components/common/LoadingState';
import ExportTableButton from '../components/common/ExportTableButton';

const QUICK_TEMPLATES = [
  {
    label: 'Login / Account Help',
    text: 'Hello! Please make sure you are using your registered mobile number and OTP. If the issue persists, try resetting your password or clearing browser cache.',
  },
  {
    label: 'Product Listing Guide',
    text: 'Hello! To add or edit your products, log in to your account, visit "My Products", and click "Add New Product" with clear photos and descriptions.',
  },
  {
    label: 'Return / Refund Policy',
    text: 'Hello! Please review our buyer and seller guidelines. For verified returns or transaction assistance, our support team will coordinate with the respective seller.',
  },
  {
    label: 'Under Investigation',
    text: 'Hello! We have received your request and our compliance & technical team is investigating this. We will follow up with you within 24 hours.',
  },
];

const HelpRequestsView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Data states
  const [loading, setLoading] = useState(false);
  const [helpList, setHelpList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);

  // Filters & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modals & Action states
  const [selectedItem, setSelectedItem] = useState(null);
  const [openReplyModal, setOpenReplyModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [alertInfo, setAlertInfo] = useState(null);

  // Fetch help requests from backend
  const fetchHelpRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      };

      const res = await axiosInstance.get('/admin/help', { params });
      if (res.data) {
        setHelpList(res.data.data || []);
        setTotalCount(res.data.total || 0);
        setPendingCount(res.data.pending || 0);
        setAnsweredCount(res.data.answered || 0);
      }
    } catch (err) {
      console.error('Failed to fetch help requests:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchHelpRequests();
  }, [fetchHelpRequests]);

  // Open Reply Modal
  const handleOpenReply = (item) => {
    setSelectedItem(item);
    setReplyText(item.reply_message || '');
    setOpenReplyModal(true);
    setAlertInfo(null);
  };

  // Submit Reply & Send Email
  const handleSubmitReply = async () => {
    if (!replyText.trim()) {
      setAlertInfo({ severity: 'error', message: 'Please enter a reply message.' });
      return;
    }

    setSubmittingReply(true);
    setAlertInfo(null);
    try {
      const res = await axiosInstance.post(`/admin/help/${selectedItem._id}/reply`, {
        reply_message: replyText.trim(),
      });

      setAlertInfo({
        severity: res.data?.emailSent ? 'success' : 'warning',
        message: res.data?.msg || 'Reply submitted successfully.',
      });

      // Update local state
      fetchHelpRequests();
      setTimeout(() => {
        setOpenReplyModal(false);
      }, 1200);
    } catch (err) {
      console.error('Failed to submit reply:', err);
      setAlertInfo({
        severity: 'error',
        message: err.response?.data?.msg || 'Failed to submit reply. Please try again.',
      });
    } finally {
      setSubmittingReply(false);
    }
  };

  // Open Delete Modal
  const handleOpenDelete = (item) => {
    setSelectedItem(item);
    setOpenDeleteModal(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/admin/help/${selectedItem._id}`);
      setOpenDeleteModal(false);
      fetchHelpRequests();
    } catch (err) {
      console.error('Failed to delete help request:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Help & Support Inquiries"
        subtitle="Review inquiries submitted by users, compose answers, and dispatch official email replies."
        count={totalCount}
        countLabel="inquiries"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Engagement', path: '/enquiries' },
          { label: 'Help & Support' },
        ]}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ExportTableButton
              data={helpList}
              total={totalCount}
              onExportAll={async () => {
                const params = {
                  all: true,
                  search: searchTerm.trim() || undefined,
                  status: statusFilter !== 'all' ? statusFilter : undefined,
                };
                const res = await axiosInstance.get('/admin/help', { params });
                return res.data.data || [];
              }}
              columns={[
                { label: 'Name', value: (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.name || 'N/A' },
                { label: 'Email', key: 'email' },
                { label: 'Phone', key: 'phone' },
                { label: 'Subject', key: 'subject' },
                { label: 'Help Request', value: (r) => r.help_request || r.message || 'N/A' },
                { label: 'Status', key: 'status' },
                { label: 'Response', value: (r) => r.response || r.answer || 'N/A' },
                { label: 'Date', value: (r) => r.createdAt || r.created_at ? new Date(r.createdAt || r.created_at).toLocaleString() : 'N/A' },
              ]}
              filename="help_requests"
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshCw size={16} />}
              onClick={fetchHelpRequests}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        }
      />

      {/* KPI Stats Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Total Inquiries
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', mt: 0.5 }}>
                  {totalCount}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: 'primary.main',
                  width: 48,
                  height: 48,
                }}
              >
                <HelpCircle size={26} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.warning.main, 0.08),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Pending Response
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.dark', mt: 0.5 }}>
                  {pendingCount}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  color: 'warning.dark',
                  width: 48,
                  height: 48,
                }}
              >
                <Clock size={26} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Answered & Emailed
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.dark', mt: 0.5 }}>
                  {answeredCount}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  color: 'success.dark',
                  width: 48,
                  height: 48,
                }}
              >
                <CheckCircle2 size={26} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Card */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {/* Filter and Search Bar */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          {/* Status Tabs */}
          <Tabs
            value={statusFilter}
            onChange={(e, val) => {
              setStatusFilter(val);
              setPage(0);
            }}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
              },
            }}
          >
            <Tab label="All" value="all" />
            <Tab
              label={
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <span>Pending</span>
                  {pendingCount > 0 && (
                    <Chip
                      label={pendingCount}
                      size="small"
                      color="warning"
                      sx={{ height: 18, fontSize: '0.75rem', fontWeight: 700 }}
                    />
                  )}
                </Stack>
              }
              value="pending"
            />
            <Tab label="Answered" value="answered" />
          </Tabs>

          {/* Search Box */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#f5f7fa',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              width: { xs: '100%', sm: 280 },
              border: '1px solid #e0e0e0',
            }}
          >
            <Search size={18} color="#78909c" />
            <InputBase
              placeholder="Search user, email, issue..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              sx={{ ml: 1, flex: 1, fontSize: '0.9rem' }}
            />
          </Box>
        </Box>

        {/* Inquiries Table */}
        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead sx={{ bgcolor: '#fafafa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>User Info</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Help Request</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Response Details</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingSkeleton rows={rowsPerPage} columns={6} />
              ) : helpList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0 }}>
                    <EmptyState
                      icon={HelpCircle}
                      title="No help requests found"
                      description={searchTerm ? 'Try modifying your search keywords.' : 'New user help inquiries will show up here.'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                helpList.map((item) => {
                  const isAnswered = item.status === 'answered';
                  return (
                    <TableRow
                      key={item._id}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        bgcolor: !isAnswered ? alpha(theme.palette.warning.main, 0.02) : 'inherit',
                      }}
                    >
                      {/* User Info */}
                      <TableCell>
                        <Stack spacing={0.4}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <User size={15} color="#546e7a" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {item.name}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Mail size={13} color="#78909c" />
                            <Typography
                              component="a"
                              href={`mailto:${item.email}`}
                              variant="caption"
                              sx={{ color: 'primary.main', textDecoration: 'none' }}
                            >
                              {item.email}
                            </Typography>
                          </Stack>
                          {item.phone && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Phone size={13} color="#78909c" />
                              <Typography
                                component="a"
                                href={`tel:${item.phone}`}
                                variant="caption"
                                sx={{ color: 'text.secondary', textDecoration: 'none' }}
                              >
                                {item.phone}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Help Request */}
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {item.help_message}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          icon={
                            isAnswered ? (
                              <CheckCircle2 size={14} color="#2e7d32" />
                            ) : (
                              <Clock size={14} color="#ed6c02" />
                            )
                          }
                          label={isAnswered ? 'Answered' : 'Pending'}
                          color={isAnswered ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                        />
                      </TableCell>

                      {/* Response Details */}
                      <TableCell sx={{ maxWidth: 260 }}>
                        {isAnswered ? (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                fontStyle: 'italic',
                              }}
                            >
                              "{item.reply_message}"
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.3 }}>
                              By: {item.replied_by || 'Admin'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                            Awaiting response...
                          </Typography>
                        )}
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          }) : '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                          {item.created_at ? new Date(item.created_at).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                          }) : ''}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant={isAnswered ? 'outlined' : 'contained'}
                            color="primary"
                            startIcon={isAnswered ? <FileText size={15} /> : <Send size={15} />}
                            onClick={() => handleOpenReply(item)}
                            sx={{
                              borderRadius: 2,
                              fontSize: '0.8rem',
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            {isAnswered ? 'Edit Answer' : 'Answer'}
                          </Button>

                          <Tooltip title="Delete Inquiry">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDelete(item)}
                              sx={{ '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 1.5 } }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Reply Modal */}
      <AdminDialog
        open={openReplyModal}
        onClose={() => !submittingReply && setOpenReplyModal(false)}
        maxWidth="md"
      >
        <AdminDialogHeader
          title={selectedItem?.status === 'answered' ? 'Update Answer & Resend Email' : 'Answer Customer Help Request'}
          subtitle="Your answer will be saved and dispatched as an email notification to the customer"
          icon={MessageSquare}
          onClose={() => !submittingReply && setOpenReplyModal(false)}
        />

        <AdminDialogContent dividers>
          {alertInfo && (
            <Alert severity={alertInfo.severity} sx={{ mb: 2.5, borderRadius: 2 }}>
              {alertInfo.message}
            </Alert>
          )}

          {/* User Details Box */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.neutral',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2.5,
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Customer Name
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {selectedItem?.name}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Email Address (Notification Target)
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {selectedItem?.email}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Phone
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {selectedItem?.phone || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Customer's Question Box */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>
              Customer's Inquiry / Issue:
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: 'primary.lighter',
                borderRadius: 2,
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                fontStyle: 'italic',
                color: 'text.primary',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
              }}
            >
              "{selectedItem?.help_message}"
            </Box>
          </Box>

          {/* Quick Response Templates */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
              Quick Templates (Click to insert):
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {QUICK_TEMPLATES.map((tmpl, idx) => (
                <Chip
                  key={idx}
                  label={tmpl.label}
                  size="small"
                  variant="outlined"
                  onClick={() => setReplyText(tmpl.text)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Reply Textarea */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
              Your Official Response: <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={5}
              placeholder="Type your response here. This will be sent directly to the customer's email..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              disabled={submittingReply}
              variant="outlined"
            />
          </Box>

          {/* Notice */}
          <Alert severity="info" sx={{ mt: 2.5, borderRadius: 2 }}>
            Submitting this answer will immediately trigger an automated branded email from{' '}
            <strong>support@resellerbazzar.in</strong> to <strong>{selectedItem?.email}</strong>.
          </Alert>
        </AdminDialogContent>

        <AdminDialogActions>
          <Button
            onClick={() => setOpenReplyModal(false)}
            disabled={submittingReply}
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReply}
            disabled={submittingReply || !replyText.trim()}
            variant="contained"
            color="primary"
            startIcon={submittingReply ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
            sx={{ px: 3, fontWeight: 700 }}
          >
            {submittingReply ? 'Sending Email & Saving...' : 'Send Answer & Email Customer'}
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmDialog
        open={openDeleteModal}
        onClose={() => !deleting && setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        itemName={selectedItem?.name}
        itemType="Help Inquiry"
        loading={deleting}
      />
    </Box>
  );
};

export default HelpRequestsView;
